import { Navigate, RouteObject, useParams } from 'react-router-dom';
import { loadBulkDraftPage } from '~/api/data/email-bulk-send/queries';
import { loadEmailThread, loadEmailThreads, loadMailboxAccounts } from '~/api/data/mailbox/queries';

import { merge } from '@oysterjs/core/graphql/loaders';

import { FEATURE_FLAGS } from '~/components/feature-flags';
import { FlagGuard } from '~/components/flag-guard';
import { OUTGOING_PATH } from '~/components/mailbox/bulk-send-paths';
import { MailboxGuard } from '~/components/mailbox/mailbox-guard';
import { BulkDraftLoadError, BulkDraftPage } from '~/pages/mailbox/bulk-draft';
import { EmailThreadPage, EmailThreadsPage, MailboxPage } from '~/pages/mailbox/emails';
import { OutboxPage } from '~/pages/mailbox/outbox';

function RedirectEmailThreadToInbox() {
  const { id } = useParams();
  return <Navigate to={`/app/mailbox/inbox/${id}`} replace />;
}

function RedirectLegacyEmailsToMailbox() {
  const { '*': splat } = useParams();
  return <Navigate to={splat ? `/app/mailbox/${splat}` : '/app/mailbox'} replace />;
}

/**
 * The mail section, split out of the router so the paths and their redirects
 * can be exercised as one tree rather than re-declared in a test.
 */
export const mailboxRoutes: RouteObject = {
  path: 'mailbox',
  element: <MailboxPage />,
  children: [
    {
      index: true,
      element: <Navigate to="inbox" replace />
    },
    {
      path: 'inbox',
      loader: merge({
        emailThreads: loadEmailThreads
      }),
      element: <EmailThreadsPage />,
      children: [
        {
          path: ':id',
          loader: merge({
            emailThread: loadEmailThread
          }),
          element: <EmailThreadPage />
        }
      ]
    },
    // A pathless layout so the guard wraps the outbound pages without adding a
    // URL segment.
    {
      element: <MailboxGuard to="/app/mailbox/inbox" />,
      children: [
        {
          path: 'outgoing',
          element: <OutboxPage />
        },
        {
          path: 'outbox',
          element: <Navigate to="/app/mailbox/outgoing" replace />
        },
        {
          path: 'drafts',
          element: <Navigate to="/app/mailbox/outgoing" replace />
        },
        {
          path: 'sent',
          element: <Navigate to="/app/mailbox/outgoing" replace />
        }
      ]
    },
    {
      path: ':id',
      element: <RedirectEmailThreadToInbox />
    }
  ]
};

/**
 * A page of its own rather than a child of `mailboxRoutes`, whose layout would put the
 * mailbox heading and tabs above it.
 */
export const bulkDraftRoute: RouteObject = {
  element: <FlagGuard flag={FEATURE_FLAGS.MAILBOX_BULK_SEND} to={OUTGOING_PATH} />,
  children: [
    {
      element: <MailboxGuard to="/app/mailbox/inbox" />,
      children: [
        {
          path: 'mailbox/outgoing/bulk/:bulkSendId',
          loader: merge({
            bulkDraftPage: loadBulkDraftPage,
            mailboxAccounts: loadMailboxAccounts
          }),
          element: <BulkDraftPage />,
          errorElement: <BulkDraftLoadError />
        }
      ]
    }
  ]
};

/**
 * Bookmarks and links sent before the section moved under `mailbox`. The splat
 * covers every depth at once, including the thread ID that `mailbox/:id` then
 * forwards on to the inbox.
 */
export const legacyEmailsRoute: RouteObject = {
  path: 'emails/*',
  element: <RedirectLegacyEmailsToMailbox />
};
